import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaSave, FaExternalLinkAlt, FaUndo } from 'react-icons/fa';
import { api } from '../../App';
import { AdminLayout } from './AdminDashboard';
import { translations } from '../../translations';
import './Admin.css';

// Pages that can be edited, with the public route for the preview link.
const PAGES = [
  { id: 'about', label: 'O nas (About)', route: '/about' },
  { id: 'fcf', label: 'First Crypto Fund', route: '/first-crypto-fund' },
];

// A few hand-written labels; everything else is derived from the key.
const FRIENDLY_LABELS = {
  seoTitle: 'SEO naslov',
  seoDescription: 'SEO opis',
};

// "profileText1" -> "Profile text 1"
const deriveLabel = (key) => {
  if (FRIENDLY_LABELS[key]) return FRIENDLY_LABELS[key];
  const spaced = key
    .replace(/([A-Z])/g, ' $1')
    .replace(/([0-9]+)/g, ' $1')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Long values get a textarea instead of a single-line input.
const isLongField = (key, value) =>
  (value && value.length > 80) ||
  /(desc|text|bio|description|intro|conclusion|strategyp|notice|tagline)/i.test(key);

const AdminContent = () => {
  const [page, setPage] = useState('about');
  const [values, setValues] = useState({ en: {}, sl: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const defaults = {
    en: translations.en[page] || {},
    sl: translations.sl[page] || {},
  };
  const fieldKeys = Object.keys(defaults.en);

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await api.get(`/api/admin/content/${page}`);
      const saved = data.content || {};
      // Prefill each field with the saved override, else the translations.js default.
      const build = (lang) => {
        const out = {};
        Object.keys(translations[lang][page] || {}).forEach((key) => {
          out[key] = saved?.[lang]?.[key] ?? translations[lang][page][key];
        });
        return out;
      };
      setValues({ en: build('en'), sl: build('sl') });
    } catch (err) {
      setError(err.message || 'Napaka pri nalaganju vsebine');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const handleChange = (lang, key, value) => {
    setValues((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
    setSuccess('');
  };

  const resetField = (key) => {
    setValues((prev) => ({
      en: { ...prev.en, [key]: defaults.en[key] },
      sl: { ...prev.sl, [key]: defaults.sl[key] },
    }));
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Only store fields that differ from the default, so unchanged text keeps
    // falling back to translations.js.
    const content = { en: {}, sl: {} };
    ['en', 'sl'].forEach((lang) => {
      fieldKeys.forEach((key) => {
        const current = (values[lang][key] ?? '').trim();
        if (current && current !== (defaults[lang][key] || '').trim()) {
          content[lang][key] = values[lang][key];
        }
      });
    });

    try {
      await api.post(`/api/admin/content/${page}`, { content });
      setSuccess('Vsebina uspešno shranjena');
    } catch (err) {
      setError(err.message || 'Napaka pri shranjevanju vsebine');
    } finally {
      setSaving(false);
    }
  };

  const activePage = PAGES.find((p) => p.id === page);

  return (
    <AdminLayout title="Vsebina strani">
      {/* Page selector */}
      <div className="content-page-tabs">
        {PAGES.map((p) => (
          <button
            key={p.id}
            className={`content-page-tab ${page === p.id ? 'active' : ''}`}
            onClick={() => setPage(p.id)}
            type="button"
          >
            {p.label}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Nalaganje vsebine...</p>
        </div>
      ) : (
        <form onSubmit={handleSave}>
          <div className="admin-card">
            <div className="content-editor-header">
              <p className="content-editor-hint">
                Uredite besedilo v angleščini (EN) in slovenščini (SL). Prazno polje
                uporabi privzeto besedilo.
              </p>
              <Link to={activePage.route} className="btn btn-outline btn-sm" target="_blank">
                <FaExternalLinkAlt style={{ marginRight: '6px' }} />
                Predogled
              </Link>
            </div>

            {fieldKeys.map((key) => {
              const long = isLongField(key, defaults.en[key]);
              const changed =
                (values.en[key] ?? '') !== (defaults.en[key] ?? '') ||
                (values.sl[key] ?? '') !== (defaults.sl[key] ?? '');
              return (
                <div className="content-field" key={key}>
                  <div className="content-field-label">
                    <span>{deriveLabel(key)}</span>
                    {changed && (
                      <button
                        type="button"
                        className="content-reset-btn"
                        onClick={() => resetField(key)}
                        title="Ponastavi na privzeto"
                      >
                        <FaUndo /> Ponastavi
                      </button>
                    )}
                  </div>
                  <div className="content-field-inputs">
                    <div className="content-field-lang">
                      <span className="lang-badge">EN</span>
                      {long ? (
                        <textarea
                          className="form-input"
                          rows={3}
                          value={values.en[key] ?? ''}
                          onChange={(e) => handleChange('en', key, e.target.value)}
                        />
                      ) : (
                        <input
                          className="form-input"
                          type="text"
                          value={values.en[key] ?? ''}
                          onChange={(e) => handleChange('en', key, e.target.value)}
                        />
                      )}
                    </div>
                    <div className="content-field-lang">
                      <span className="lang-badge">SL</span>
                      {long ? (
                        <textarea
                          className="form-input"
                          rows={3}
                          value={values.sl[key] ?? ''}
                          onChange={(e) => handleChange('sl', key, e.target.value)}
                        />
                      ) : (
                        <input
                          className="form-input"
                          type="text"
                          value={values.sl[key] ?? ''}
                          onChange={(e) => handleChange('sl', key, e.target.value)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="content-save-bar">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <FaSave style={{ marginRight: '8px' }} />
              {saving ? 'Shranjevanje...' : 'Shrani spremembe'}
            </button>
          </div>
        </form>
      )}
    </AdminLayout>
  );
};

export default AdminContent;
